import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { concat, forkJoin, of } from 'rxjs';
import { catchError, toArray } from 'rxjs/operators';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { ParkingZoneService } from '../../../api/gen/api/parkingZone.service';
import { ParkerService } from '../../../api/gen/api/parker.service';
import { PoolParkingConfigDto } from '../../../api/gen/model/poolParkingConfigDto';
import { UpdatePoolParkingConfigRequest } from '../../../api/gen/model/updatePoolParkingConfigRequest';
import { PoolOverflowBehavior } from '../../../api/gen/model/poolOverflowBehavior';
import { OrganizationDto } from '../../../api/gen/model/organizationDto';
import { RegisteredParkerDto } from '../../../api/gen/model/registeredParkerDto';
import { ParkerDto } from '../../../api/gen/model/parkerDto';
import { ParkingZoneDto } from '../../../api/gen/model/parkingZoneDto';
import { OrganizationZoneDto } from '../../../api/gen/model/organizationZoneDto';
import { AccessWindowFieldComponent, AccessWindowValue } from '../../shared/access-window-field/access-window-field.component';
import { JJamesAlertComponent, toDateInputValue, dateInputValueToIso } from 'shared-lib';

@Component({
    selector: 'app-pool-config-edit',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        JJamesAlertComponent,
        AccessWindowFieldComponent
    ],
    templateUrl: './pool-config-edit.component.html',
    styleUrls: ['./pool-config-edit.component.scss']
})
export class PoolConfigEditComponent implements OnInit {
    private readonly orgService = inject(OrganizationService);
    private readonly parkingZoneService = inject(ParkingZoneService);
    private readonly parkerService = inject(ParkerService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);

    orgId = signal<string | null>(null);
    configId = signal<string | null>(null);

    config = signal<PoolParkingConfigDto | null>(null);
    organization = signal<OrganizationDto | null>(null);

    loading = signal(false);
    zoneActionLoading = signal(false);

    errorMessage = signal<string | null>(null);
    successMessage = signal<string | null>(null);

    /** Org zones not yet assigned to this config (from OrganizationDto.parkingZones). */
    availableZoneIds = signal<OrganizationZoneDto[]>([]);
    selectedZoneIdToAdd = signal<string>('');
    zoneNames = signal<Map<string, string>>(new Map());

    deleteZoneConfirmId = signal<string | null>(null);

    showEditForm = signal(false);
    editName = signal('');
    editDescription = signal('');
    editMaxCars = signal<number | null>(null);
    editValidFrom = signal('');
    editValidTo = signal('');
    editOverflowBehavior = signal<PoolOverflowBehavior>(PoolOverflowBehavior.Deny);
    editWindow = signal<AccessWindowValue>({ start: null, end: null, days: null });
    editLoading = signal(false);

    /** Pool-full overflow policy options (matches the add form). */
    readonly overflowBehaviors: ReadonlyArray<{ value: PoolOverflowBehavior; label: string; hint: string }> = [
        { value: PoolOverflowBehavior.Deny, label: 'Deny', hint: 'Pool full → admit as short-term (billed at the short-term tariff) if the space offers it, otherwise refuse entry.' },
        { value: PoolOverflowBehavior.OrgPays, label: 'Organization pays', hint: 'Admit the overflow stay; the organization is billed at the pool tariff and the gate opens free to the parker.' },
        { value: PoolOverflowBehavior.ParkerPays, label: 'Parker pays', hint: 'Admit the overflow stay; the parker pays the pool tariff at exit (gate held until paid).' }
    ];

    /** Friendly label for the static (non-edit) view. */
    overflowBehaviorLabel(value: PoolOverflowBehavior | null | undefined): string {
        return this.overflowBehaviors.find(o => o.value === value)?.label ?? 'Deny';
    }

    /** Org parkers (with names) not yet assigned to this pool config. */
    availableParkers = signal<ParkerDto[]>([]);
    selectedParkerIdsToAdd = signal<string[]>([]);
    addProgress = signal('');
    parkerActionLoading = signal(false);
    deleteParkerConfirmId = signal<string | null>(null);

    ngOnInit(): void {
        this.orgId.set(this.route.snapshot.paramMap.get('orgId'));
        this.configId.set(this.route.snapshot.paramMap.get('configId'));
        if (this.orgId() && this.configId()) {
            this.load();
        } else {
            this.errorMessage.set('Missing org or config ID.');
        }
    }

    load(): void {
        const orgId = this.orgId()!;
        const configId = this.configId()!;
        this.loading.set(true);
        this.errorMessage.set(null);

        this.orgService.organizationsOrganizationIdPoolParkingConfigsConfigIdGet(orgId, configId, false).subscribe({
            next: (cfg) => {
                this.config.set(cfg);
                this.loadOrg(orgId, cfg);
            },
            error: (err: any) => {
                this.errorMessage.set(err.error?.title || 'Failed to load pool config.');
                this.loading.set(false);
            }
        });
    }

    private loadOrg(orgId: string, cfg: PoolParkingConfigDto): void {
        this.orgService.organizationsOrganizationIdFetchPost(orgId, {
            includeDeleted: false,
            includeValidity: true
        }).subscribe({
            next: (org) => {
                this.organization.set(org);
                this.loadParkerNames(org, cfg);
                this.updateAvailableZones(cfg, org.parkingZones ?? []);
                this.loadZoneNames(org.parkingZones ?? []);
                this.loading.set(false);
            },
            error: (err: any) => {
                this.errorMessage.set(err.error?.title || 'Failed to load organization.');
                this.loading.set(false);
            }
        });
    }

    private loadZoneNames(orgZones: OrganizationZoneDto[]): void {
        const ids = orgZones.map(z => z.parkingZoneId).filter((id): id is string => !!id);
        if (!ids.length) return;
        const existing = this.zoneNames();
        if (ids.every(id => existing.has(id))) return;
        const requests = ids.map(id =>
            this.parkingZoneService.parkingZoneParkingZoneIdGet(id).pipe(catchError(() => of(null)))
        );
        forkJoin(requests).subscribe(results => {
            const map = new Map(existing);
            results.forEach((dto, i) => { if (dto) map.set(ids[i], dto.name ?? ids[i]); });
            this.zoneNames.set(map);
        });
    }

    getZoneName(parkingZoneId: string): string {
        return this.zoneNames().get(parkingZoneId) ?? parkingZoneId;
    }

    private updateAvailableZones(cfg: PoolParkingConfigDto, orgZones: OrganizationZoneDto[]): void {
        const assignedIds = new Set(cfg.allowedZoneIds ?? []);
        const available = orgZones.filter(z => !!z.parkingZoneId && !assignedIds.has(z.parkingZoneId));
        this.availableZoneIds.set(available);
        this.selectedZoneIdToAdd.set(available[0]?.parkingZoneId ?? '');
    }

    private loadParkerNames(org: OrganizationDto, cfg: PoolParkingConfigDto): void {
        const parkerIds = (org.parkers ?? []).map(p => p.parkerId).filter((id): id is string => !!id);
        if (parkerIds.length === 0) {
            this.updateAvailableParkers(cfg, []);
            return;
        }
        this.parkerService.operatorParkerByIdsPost({ parkerIds }).subscribe({
            next: (parkers) => {
                this.updateAvailableParkers(cfg, parkers);
            },
            error: () => {
                this.updateAvailableParkers(cfg, []);
            }
        });
    }

    private updateAvailableParkers(cfg: PoolParkingConfigDto, orgParkers: ParkerDto[]): void {
        const assigned = new Set(cfg.parkerIds ?? []);
        const available = orgParkers.filter(p => !!p.id && !assigned.has(p.id));
        this.availableParkers.set(available);
        this.selectedParkerIdsToAdd.set([]);
    }

    get allowedZones(): ParkingZoneDto[] {
        return this.config()?.allowedZones ?? [];
    }

    get hasNoZones(): boolean {
        return (this.organization()?.parkingZones?.length ?? 0) === 0;
    }

    /**
     * True when THIS pool config has no allowed zone selected.
     *
     * Distinct from hasNoZones, which asks whether the organization holds any zone at all. An org
     * can own several zones while this config still has none picked, and the parker controls used
     * to key off the org-level check - so adding a parker there minted a parking right covering no
     * zone, a member who cannot open any gate and whose problem only surfaces at the barrier.
     */
    get hasNoAllowedZones(): boolean {
        return (this.config()?.allowedZoneIds ?? []).length === 0;
    }

    get assignedParkers(): RegisteredParkerDto[] {
        return this.config()?.parkers ?? [];
    }

    /**
     * Label for a parker in the "add parker" dropdown: their name, else a plate, else the id.
     *
     * firstName/lastName are newly added to ParkerDto — they lived on RegisteredParkerDto, where
     * serialization silently dropped them from every List<ParkerDto> response, so this dropdown
     * could only ever offer plates and GUIDs. Read regen-safely so it compiles until
     * `npm run generate:api:operator` runs.
     */
    parkerOptionLabel(parker: ParkerDto): string {
        const named = parker as ParkerDto & { firstName?: string; lastName?: string };
        const name = [named.firstName, named.lastName]
            .map(part => part?.trim())
            .filter(Boolean)
            .join(' ');
        if (name) {
            // Plate alongside the name: two colleagues can share a name, and the plate is what the
            // operator is usually holding when they come to this screen.
            const plate = parker.activeLicenses?.[0]?.fullNumber;
            return plate ? `${name} — ${plate}` : name;
        }
        return parker.activeLicenses?.[0]?.fullNumber ?? parker.id ?? '';
    }

    /** Human-readable pool-access window for the static view. */
    get windowSummary(): string {
        const cfg = this.config();
        if (!cfg?.poolAccessWindowStart || !cfg?.poolAccessWindowEnd) return '24/7 (no window)';
        const fmt = (t: string) => t.substring(0, 5);
        const time = `${fmt(cfg.poolAccessWindowStart)}–${fmt(cfg.poolAccessWindowEnd)}`;
        const days = cfg.poolAccessWindowDays?.length
            ? cfg.poolAccessWindowDays.map(d => d.substring(0, 3)).join(', ')
            : 'every day';
        return `${time} · ${days}`;
    }

    get hasNoParkers(): boolean {
        return (this.organization()?.parkers?.length ?? 0) === 0;
    }

    addZone(): void {
        const zoneId = this.selectedZoneIdToAdd();
        if (!zoneId || !this.orgId() || !this.configId()) return;

        this.zoneActionLoading.set(true);
        this.errorMessage.set(null);

        this.orgService.organizationsOrganizationIdPoolParkingConfigsConfigIdZonesPost(
            this.orgId()!, this.configId()!, { zoneId }
        ).subscribe({
            next: () => {
                this.successMessage.set('Zone added.');
                this.zoneActionLoading.set(false);
                this.load();
            },
            error: (err: any) => {
                this.errorMessage.set('Failed to add zone: ' + (err.error ?? 'Unknown error'));
                this.zoneActionLoading.set(false);
            }
        });
    }

    confirmRemoveZone(zoneId: string): void { this.deleteZoneConfirmId.set(zoneId); }
    cancelRemoveZone(): void { this.deleteZoneConfirmId.set(null); }

    removeZone(zoneId: string): void {
        if (!this.orgId() || !this.configId()) return;

        this.zoneActionLoading.set(true);
        this.deleteZoneConfirmId.set(null);
        this.errorMessage.set(null);

        this.orgService.organizationsOrganizationIdPoolParkingConfigsConfigIdZonesDelete(
            this.orgId()!, this.configId()!, { zoneId }
        ).subscribe({
            next: () => {
                this.successMessage.set('Zone removed.');
                this.zoneActionLoading.set(false);
                this.load();
            },
            error: (err: any) => {
                this.errorMessage.set(err.error?.title || 'Failed to remove zone.');
                this.zoneActionLoading.set(false);
            }
        });
    }

    onParkerSelectionChange(event: Event): void {
        const options = Array.from((event.target as HTMLSelectElement).selectedOptions);
        this.selectedParkerIdsToAdd.set(options.map(o => o.value).filter(v => !!v));
    }

    selectAllAvailableParkers(): void {
        this.selectedParkerIdsToAdd.set(this.availableParkers().map(p => p.id!).filter(id => !!id));
    }

    /**
     * Add every selected parker, then reload ONCE.
     *
     * Sequential rather than parallel: each POST also generates a parking right against the same
     * pool config, and firing them concurrently races on that aggregate. The win here is not
     * concurrency - it is that the old flow called load() after every single parker, so adding ten
     * meant ten full config reloads. Failures are collected and reported instead of aborting the
     * rest, so one bad parker does not strand the other nine.
     */
    addSelectedParkers(): void {
        const ids = this.selectedParkerIdsToAdd();
        const orgId = this.orgId();
        const configId = this.configId();
        // Re-checked here and not only on the button: the zone list can empty out from the other
        // column while a selection is already sitting in this one.
        if (ids.length === 0 || !orgId || !configId || this.hasNoAllowedZones) return;

        this.parkerActionLoading.set(true);
        this.errorMessage.set(null);
        this.successMessage.set(null);

        let done = 0;
        const failed: string[] = [];
        this.addProgress.set(`0/${ids.length}`);

        const calls = ids.map(id =>
            this.orgService
                .organizationsOrganizationIdPoolParkingConfigsConfigIdParkersParkerIdPost(orgId, configId, id)
                .pipe(catchError((err: any) => {
                    failed.push(this.parkerLabelById(id) + (err?.error?.title ? `: ${err.error.title}` : ""));
                    return of(null);
                }))
        );

        concat(...calls).pipe(toArray()).subscribe({
            next: () => { done = ids.length; this.addProgress.set(`${done}/${ids.length}`); },
            error: () => {
                this.errorMessage.set("Failed to add parkers.");
                this.parkerActionLoading.set(false);
                this.addProgress.set("");
            },
            complete: () => {
                const added = ids.length - failed.length;
                if (failed.length === 0) {
                    this.successMessage.set(`${added} parker${added === 1 ? "" : "s"} added — parking rights generated for their license plates.`);
                } else {
                    this.errorMessage.set(`${added} of ${ids.length} added. Failed: ${failed.join("; ")}`);
                }
                this.parkerActionLoading.set(false);
                this.addProgress.set("");
                this.load();
            }
        });
    }

    private parkerLabelById(id: string): string {
        const p = this.availableParkers().find(x => x.id === id);
        return p ? this.parkerOptionLabel(p) : id;
    }

    confirmRemoveParker(parkerId: string): void { this.deleteParkerConfirmId.set(parkerId); }
    cancelRemoveParker(): void { this.deleteParkerConfirmId.set(null); }

    removeParker(parkerId: string): void {
        if (!this.orgId() || !this.configId()) return;

        this.parkerActionLoading.set(true);
        this.deleteParkerConfirmId.set(null);
        this.errorMessage.set(null);

        this.orgService
            .organizationsOrganizationIdPoolParkingConfigsConfigIdParkersParkerIdDelete(
                this.orgId()!, this.configId()!, parkerId
            )
            .subscribe({
                next: () => {
                    this.successMessage.set('Parker removed — their pool parking right was revoked.');
                    this.parkerActionLoading.set(false);
                    this.load();
                },
                error: (err: any) => {
                    this.errorMessage.set(err.error?.title || 'Failed to remove parker.');
                    this.parkerActionLoading.set(false);
                }
            });
    }

    startEdit(): void {
        const cfg = this.config();
        if (!cfg) return;
        this.editName.set(cfg.name ?? '');
        this.editDescription.set(cfg.description ?? '');
        this.editMaxCars.set(cfg.maxCarsAllowed ?? null);
        // Local date, not the UTC slice. validFrom comes back as an instant (e.g. 2021-02-06T23:00:00Z
        // = Feb 7th locally in CET); substring(0, 10) would show Feb 6th, and saving that back stored
        // Feb 6th local — so merely opening this form and pressing save walked the date one day
        // earlier, every time, and now cascades onto every member's parking right.
        this.editValidFrom.set(toDateInputValue(cfg.validFrom));
        this.editValidTo.set(toDateInputValue(cfg.validTo));
        this.editOverflowBehavior.set(cfg.overflowBehavior ?? PoolOverflowBehavior.Deny);
        this.editWindow.set({
            start: cfg.poolAccessWindowStart ?? null,
            end: cfg.poolAccessWindowEnd ?? null,
            days: cfg.poolAccessWindowDays ?? null
        });
        this.showEditForm.set(true);
    }

    cancelEdit(): void {
        this.showEditForm.set(false);
    }

    saveEdit(): void {
        if (!this.orgId() || !this.configId()) return;
        this.editLoading.set(true);
        this.errorMessage.set(null);
        const req: UpdatePoolParkingConfigRequest = {
            name: this.editName() || undefined,
            description: this.editDescription() || null,
            maxCarsAllowed: this.editMaxCars() ?? undefined,
            // Sent as an explicit instant rather than a bare YYYY-MM-DD, so the stored value is fixed
            // by the operator's timezone and not by whatever the API server's happens to be.
            validFrom: dateInputValueToIso(this.editValidFrom()) ?? undefined,
            validTo: dateInputValueToIso(this.editValidTo()),
            overflowBehavior: this.editOverflowBehavior(),
            poolAccessWindowStart: this.editWindow().start,
            poolAccessWindowEnd: this.editWindow().end,
            poolAccessWindowDays: this.editWindow().days
        };
        this.orgService.organizationsOrganizationIdPoolParkingConfigsConfigIdPut(
            this.orgId()!, this.configId()!, req
        ).subscribe({
            next: (updated) => {
                this.config.set(updated);
                this.showEditForm.set(false);
                this.editLoading.set(false);
                this.successMessage.set('Configuration updated.');
            },
            error: (err: any) => {
                this.errorMessage.set(err.error?.title || 'Failed to update configuration.');
                this.editLoading.set(false);
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/org-management', this.orgId()]);
    }
}
